import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthShell, AUTH_PRIMARY_BUTTON, AUTH_TITLE_CLASS } from '@/components/auth/AuthShell';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';

/// The emailed "Confirm your Microsoft sign-in" link lands here. The token is
/// in the fragment (never sent to a server or analytics) and the confirmation
/// waits for a click, so an email scanner opening the link doesn't use it up.
/// The API links the Microsoft account only in the browser that started the
/// sign-in, then sends the person on to /auth/callback like any sign-in.
export default function ConfirmMicrosoftPage() {
  const [token, setToken] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.substring(1));
    const t = params.get('token');
    // Out of the address bar straight away.
    try { window.history.replaceState(null, '', window.location.pathname); } catch { /* no-op */ }
    if (t) setToken(t); else setMissing(true);
  }, []);

  const confirm = () => {
    if (!token) return;
    window.location.href = `${API_URL}/auth/microsoft/confirm?token=${encodeURIComponent(token)}`;
  };

  return (
    <AuthShell width="form">
      {missing ? (
        <>
          <Alert variant="error">
            <AlertDescription>This confirmation link is incomplete. Sign in with Microsoft again for a new one.</AlertDescription>
          </Alert>
          <p className="mt-3 text-center text-sm">
            <a href="/" className="text-primary hover:underline">Return to sign in</a>
          </p>
        </>
      ) : (
        <>
          <h1 className={cn('mb-2', AUTH_TITLE_CLASS)}>Confirm your Microsoft sign-in</h1>
          <p className="mb-6 text-sm text-foreground/75">
            This links the Microsoft account you just signed in with to your Cadence account. Only continue if you
            signed in with Microsoft a moment ago, in this browser.
          </p>
          <button
            type="button"
            onClick={confirm}
            disabled={!token}
            className={AUTH_PRIMARY_BUTTON}
          >
            Confirm and sign in
          </button>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Didn’t just sign in? Close this page; nothing changes.
          </p>
        </>
      )}
    </AuthShell>
  );
}
